import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Search, Lock, ArrowRight, Clock, BookOpen } from "lucide-react";

export default function BlogsPublic() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [domain, setDomain] = useState("");
  const [tech, setTech] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = {};
    if (search) params.q = search;
    if (domain) params.domain = domain;
    if (tech) params.technology = tech;
    api
      .get("/blogs", { params })
      .then((r) => {
        if (!cancelled) setItems(r.data || []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [search, domain, tech]);

  const hero = useMemo(() => items.find((b) => b.is_featured) || items[0], [items]);
  const rest = useMemo(() => items.filter((b) => b.id !== hero?.id), [items, hero]);

  // Build unique filter chip lists from the data we have
  const domains = useMemo(() => {
    const s = new Set();
    items.forEach((b) => b.domain && s.add(b.domain));
    return Array.from(s).slice(0, 8);
  }, [items]);
  const techs = useMemo(() => {
    const s = new Set();
    items.forEach((b) => (b.technologies || []).forEach((t) => s.add(t)));
    return Array.from(s).slice(0, 8);
  }, [items]);

  return (
    <div className="min-h-screen bg-kb-bg" data-testid="blogs-public-page">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 lg:px-10 pt-16 pb-24">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">JOURNAL</div>
        <h1 className="font-serif text-4xl lg:text-6xl text-kb-ink mt-2 leading-tight">
          Field notes from the <em className="italic text-kb-primary">research lab</em>.
        </h1>
        <p className="mt-5 text-kb-ink-muted max-w-2xl">
          Essays, breakdowns, and deep-dives on the science of publication, methodology craft, and the tools we build at
          KhojByte.
        </p>

        {/* Filters */}
        <div className="mt-8 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-3 text-kb-ink-muted" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search articles, topics, tags…"
              className="kb-input text-sm pl-9 w-full"
              data-testid="blogs-search-input"
            />
          </div>
          {domains.length > 0 && (
            <select
              className="kb-input text-sm"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              data-testid="blogs-domain-filter"
            >
              <option value="">All domains</option>
              {domains.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          )}
          {techs.length > 0 && (
            <select
              className="kb-input text-sm"
              value={tech}
              onChange={(e) => setTech(e.target.value)}
              data-testid="blogs-tech-filter"
            >
              <option value="">All technologies</option>
              {techs.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          )}
        </div>

        {loading ? (
          <div className="mt-10 kb-mono text-xs text-kb-ink-muted">Loading…</div>
        ) : items.length === 0 ? (
          <div className="mt-12 text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white text-sm text-kb-ink-muted">
            No articles yet. Check back soon.
          </div>
        ) : (
          <>
            {/* HERO featured post — magazine-style */}
            {hero && (
              <Link
                to={`/blogs/${hero.slug || hero.id}`}
                className="mt-10 group block kb-card overflow-hidden hover:shadow-lg transition-shadow"
                data-testid={`blog-hero-${hero.id}`}
              >
                <div className="grid lg:grid-cols-5">
                  <div className="lg:col-span-3 h-56 lg:h-auto bg-kb-ink relative overflow-hidden">
                    {hero.cover_image_url ? (
                      <img
                        src={hero.cover_image_url}
                        alt=""
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-kb-primary/40 via-kb-ink to-kb-ink flex items-center justify-center">
                        <BookOpen className="w-20 h-20 text-white/20" strokeWidth={1} />
                      </div>
                    )}
                    <div className="absolute top-4 left-4">
                      <span className="kb-mono text-[10px] uppercase tracking-[0.2em] text-white bg-kb-primary px-3 py-1.5 rounded-sm">
                        FEATURED
                      </span>
                    </div>
                  </div>
                  <div className="lg:col-span-2 p-7 lg:p-10 flex flex-col justify-center">
                    <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary">
                      {hero.domain || "JOURNAL"} · {hero.read_minutes || 5} MIN READ
                    </div>
                    <h2 className="font-serif text-2xl lg:text-3xl text-kb-ink mt-3 leading-tight group-hover:text-kb-primary transition-colors">
                      {hero.title}
                    </h2>
                    {hero.excerpt && (
                      <p className="mt-4 text-sm text-kb-ink-muted leading-relaxed line-clamp-3">{hero.excerpt}</p>
                    )}
                    <div className="mt-6 flex items-center gap-3">
                      {hero.author_picture ? (
                        <img
                          src={hero.author_picture}
                          alt=""
                          className="w-9 h-9 rounded-full object-cover border border-slate-200"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-kb-primary/10 flex items-center justify-center kb-mono text-xs text-kb-primary">
                          {(hero.author_name || "K")[0]?.toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-kb-ink">{hero.author_name || "KhojByte Team"}</div>
                        <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
                          {hero.author_role || "Research"}
                        </div>
                      </div>
                      {hero.is_premium && (
                        <Lock className="w-4 h-4 text-kb-primary" title="Premium" />
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {/* GRID of the rest */}
            {rest.length > 0 && (
              <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blogs-grid">
                {rest.map((b) => (
                  <Link
                    to={`/blogs/${b.slug || b.id}`}
                    key={b.id}
                    className="kb-card overflow-hidden flex flex-col hover:shadow-md transition-shadow group"
                    data-testid={`blog-card-${b.id}`}
                  >
                    <div className="h-36 bg-slate-100 overflow-hidden relative">
                      {b.cover_image_url ? (
                        <img
                          src={b.cover_image_url}
                          alt=""
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-kb-primary/15 to-slate-100 flex items-center justify-center">
                          <BookOpen className="w-10 h-10 text-kb-primary/40" strokeWidth={1.2} />
                        </div>
                      )}
                    </div>
                    <div className="p-5 flex-1 flex flex-col">
                      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary inline-flex items-center gap-1">
                        {b.domain || "JOURNAL"}
                      </div>
                      <h3 className="font-serif text-lg text-kb-ink mt-2 leading-snug group-hover:text-kb-primary transition-colors line-clamp-2">
                        {b.title}
                      </h3>
                      {b.excerpt && <p className="text-sm text-kb-ink-muted mt-2 flex-1 line-clamp-3">{b.excerpt}</p>}
                      <div className="mt-4 pt-3 border-t border-slate-200 flex items-center justify-between">
                        <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted inline-flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {b.read_minutes || 5} min
                        </span>
                        <span className="kb-mono text-[10px] uppercase tracking-wider inline-flex items-center gap-1">
                          {b.locked ? (
                            <span className="inline-flex items-center gap-1 text-kb-primary">
                              <Lock className="w-3 h-3" /> PREMIUM
                            </span>
                          ) : (
                            <span className="text-kb-primary">
                              Read <ArrowRight className="w-3 h-3 inline" />
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
